// Vizuální vrstva Kolonie: SVG mapa kreslená kódem, ruka, tlačítka a panely. Jen klepání, bez hoveru.
// Pro čtečku je skrytá (aria-hidden); nevidomý hráč používá zvukovou vrstvu.
import type { GameEvent } from "../../../games/types.ts";
import { COSTS, pips, RESOURCES, type Bag, type KolonieView, type Res } from "../../../games/kolonie/meta.ts";
import { TOPOLOGY as T } from "../../../games/kolonie/topology.ts";
import { afford, myPending, rateText, targets, whatNow, type Model, type PickKind } from "./model.ts";
import { bagText, bagTotal, cards, DEV_HELP, DEV_NAME, pointsText, resName } from "./texts.ts";

export const PLAYER_COLORS = ["#c62828", "#1565c0", "#e65100", "#6a1b9a"];
const TERRAIN_FILL: Record<string, string> = { kov: "#b0bec5", sklo: "#e6d3a3", voda: "#b3e5fc", jidlo: "#c5e1a5", energie: "#fff176", krater: "#8d6e63" };
const TERRAIN_LABEL: Record<string, string> = { kov: "kov", sklo: "sklo", voda: "voda", jidlo: "jídlo", energie: "energie", krater: "kráter" };

type Panel =
  | { kind: "discard"; bag: Bag }
  | { kind: "bank"; give: Res | null }
  | { kind: "trade"; give: Bag; want: Bag; counterTo: number | null }
  | { kind: "cards" }
  | { kind: "yop"; picks: Res[] }
  | { kind: "monopoly" };

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const fmt = (n: number) => Math.round(n * 1000) / 1000;

export class VisualLayer {
  readonly root: HTMLElement;
  showCursor = false;
  private model: Model;
  private panel: Panel | null = null;
  private flash: { corner?: number; edge?: number } = {};

  constructor(model: Model, board: HTMLElement) {
    this.model = model;
    this.root = document.createElement("div");
    this.root.className = "kol";
    this.root.setAttribute("aria-hidden", "true");
    board.appendChild(this.root);
    this.root.addEventListener("click", (e) => this.click(e.target as Element));
  }

  destroy() {
    this.root.remove();
  }

  onEvents(events: GameEvent[]) {
    for (const e of events) {
      if (e.type === "built") this.flash = { corner: e.corner as number | undefined, edge: e.edge as number | undefined };
    }
  }

  // ---------- Klepání ----------

  private click(target: Element) {
    const v = this.model.view;
    if (!v || v.me === null) return;
    const el = target.closest<HTMLElement | SVGElement>("[data-act],[data-corner],[data-edge],[data-hex]");
    if (!el) return;
    const d = (el as HTMLElement).dataset;
    if (d.corner !== undefined) return this.pickCorner(Number(d.corner));
    if (d.edge !== undefined) return this.pickEdge(Number(d.edge));
    if (d.hex !== undefined) return this.model.send({ type: "moveRobber", hex: Number(d.hex) });
    this.action(d.act!, d);
  }

  private pickCorner(c: number) {
    const kind = this.model.pick;
    if (kind === "setupSettlement") this.model.send({ type: "placeSettlement", corner: c });
    else if (kind === "settlement") this.model.send({ type: "buildSettlement", corner: c });
    else if (kind === "city") this.model.send({ type: "buildCity", corner: c });
    this.model.pick = null;
  }

  private pickEdge(e: number) {
    const kind = this.model.pick;
    if (kind === "setupRoad") this.model.send({ type: "placeRoad", edge: e });
    else if (kind === "road" || kind === "freeRoad") this.model.send({ type: "buildRoad", edge: e });
    if (kind !== "freeRoad") this.model.pick = null;
  }

  private action(act: string, d: DOMStringMap) {
    const v = this.model.view!;
    const send = this.model.send;
    const res = d.res as Res | undefined;
    switch (act) {
      case "roll":
        return send({ type: "roll" });
      case "end":
        this.model.pick = null;
        return send({ type: "endTurn" });
      case "pick":
        this.model.pick = this.model.pick === d.kind ? null : (d.kind as PickKind);
        return this.render();
      case "cancelPick":
        this.model.pick = null;
        return this.render();
      case "buyDev":
        return send({ type: "buyDev" });
      case "panel":
        this.panel = this.newPanel(d.kind!);
        return this.render();
      case "close":
        this.panel = null;
        return this.render();
      case "steal":
        return send({ type: "steal", victim: Number(d.p) });
      // Odhazování
      case "discardAdd":
      case "discardSub": {
        const p = this.panel as Extract<Panel, { kind: "discard" }>;
        const n = (p.bag[res!] ?? 0) + (act === "discardAdd" ? 1 : -1);
        if (n >= 0 && n <= v.hand![res!]) p.bag[res!] = n;
        return this.render();
      }
      case "discardSend": {
        const p = this.panel as Extract<Panel, { kind: "discard" }>;
        this.panel = null;
        return send({ type: "discard", cards: p.bag });
      }
      // Banka
      case "bankGive":
        (this.panel as Extract<Panel, { kind: "bank" }>).give = res!;
        return this.render();
      case "bankGet": {
        const p = this.panel as Extract<Panel, { kind: "bank" }>;
        this.panel = null;
        return send({ type: "bankTrade", give: p.give!, get: res! });
      }
      // Obchod
      case "tradeAdd":
      case "tradeSub": {
        const p = this.panel as Extract<Panel, { kind: "trade" }>;
        const side = d.side === "give" ? p.give : p.want;
        const other = d.side === "give" ? p.want : p.give;
        let n = (side[res!] ?? 0) + (act === "tradeAdd" ? 1 : -1);
        if (d.side === "give") n = Math.min(n, this.giveLimit(p, res!));
        if (n < 0) n = 0;
        side[res!] = n;
        if (n > 0) other[res!] = 0;
        return this.render();
      }
      case "tradeSend": {
        const p = this.panel as Extract<Panel, { kind: "trade" }>;
        this.panel = null;
        if (p.counterTo !== null) return send({ type: "counterTrade", offerId: v.trade!.id, give: p.want, want: p.give });
        return send({ type: "offerTrade", give: p.give, want: p.want, to: null });
      }
      case "accept":
        return send({ type: "respondTrade", offerId: v.trade!.id, response: "accept" });
      case "decline":
        return send({ type: "respondTrade", offerId: v.trade!.id, response: "decline" });
      case "counter": {
        // Protinabídka: z pohledu odpovídajícího „dám“ = co chce nabízející a naopak.
        this.panel = { kind: "trade", give: { ...v.trade!.want }, want: { ...v.trade!.give }, counterTo: v.trade!.from };
        return this.render();
      }
      case "confirm":
        return send({ type: "confirmTrade", offerId: v.trade!.id, with: Number(d.p) });
      case "cancelTrade":
        return send({ type: "cancelTrade" });
      // Karty výzkumu
      case "play": {
        const kind = d.kind!;
        if (kind === "hlidka") {
          this.panel = null;
          return send({ type: "playKnight" });
        }
        if (kind === "tunely") {
          this.panel = null;
          this.model.pick = "freeRoad";
          return send({ type: "playRoadBuilding" });
        }
        this.panel = kind === "zasoby" ? { kind: "yop", picks: [] } : { kind: "monopoly" };
        return this.render();
      }
      case "yopPick": {
        const p = this.panel as Extract<Panel, { kind: "yop" }>;
        p.picks.push(res!);
        if (p.picks.length < 2) return this.render();
        this.panel = null;
        return send({ type: "playYearOfPlenty", res: [p.picks[0], p.picks[1]] });
      }
      case "monopolyPick":
        this.panel = null;
        return send({ type: "playMonopoly", res: res! });
    }
  }

  private giveLimit(_p: Extract<Panel, { kind: "trade" }>, r: Res): number {
    return this.model.view!.hand![r];
  }

  private newPanel(kind: string): Panel {
    switch (kind) {
      case "bank":
        return { kind: "bank", give: null };
      case "trade":
        return { kind: "trade", give: {}, want: {}, counterTo: null };
      case "cards":
        return { kind: "cards" };
      default:
        return { kind: "cards" };
    }
  }

  // ---------- Kreslení ----------

  render() {
    const v = this.model.view;
    if (!v) return;
    const mine = myPending(v);
    const kinds = mine.map((p) => p.kind);
    // Výběr na mapě podle fáze (rozestavění, piráti, tunely zdarma) se zapne sám.
    if (kinds.includes("setupSettlement")) this.model.pick = "setupSettlement";
    else if (kinds.includes("setupRoad")) this.model.pick = "setupRoad";
    else if (kinds.includes("robber")) this.model.pick = "robber";
    else if (kinds.includes("roadBuilding")) this.model.pick = "freeRoad";
    else if (!kinds.includes("main")) this.model.pick = null;
    if (kinds.includes("discard") && this.panel?.kind !== "discard") this.panel = { kind: "discard", bag: {} };
    if (!kinds.includes("discard") && this.panel?.kind === "discard") this.panel = null;
    if (!kinds.includes("main") && this.panel && this.panel.kind !== "discard" && !(this.panel.kind === "trade" && this.panel.counterTo !== null)) this.panel = null;

    // Na telefonu je mapa hned nahoře, hráči až pod ovládáním.
    this.root.innerHTML = `
      <div class="kol-hint">${esc(this.hint(v))}</div>
      ${this.mapSvg(v)}
      ${this.handHtml(v)}
      ${this.tradeHtml(v)}
      ${this.actionsHtml(v)}
      ${this.playersHtml(v)}
      ${this.panelHtml(v)}`;
    this.flash = {};
  }

  private hint(v: KolonieView): string {
    const pick = this.model.pick;
    if (pick === "road") return "Klepni na zvýrazněné místo pro tunel.";
    if (pick === "settlement") return "Klepni na zvýrazněné místo pro základnu.";
    if (pick === "city") return "Klepni na základnu, ze které bude město.";
    if (pick === "robber") return "Klepni na pole, kam přesuneš piráty.";
    return whatNow(v);
  }

  private playersHtml(v: KolonieView): string {
    return `<div class="kol-players">${v.players
      .map((p, i) => {
        const badges = [v.longestHolder === i ? "nejdelší síť" : "", v.largestHolder === i ? "největší flotila" : "", p.isBot ? "robot" : ""].filter(Boolean);
        const pts = i === v.me ? v.myPoints! : p.publicPoints;
        return `<div class="kol-player${v.turn === i && v.phase !== "over" ? " is-turn" : ""}">
          <span class="kol-swatch p${i}"></span>
          <span class="kol-pname">${esc(i === v.me ? `${p.name} (ty)` : p.name)}</span>
          <span class="kol-pinfo">${pointsText(pts)} · ${cards(p.cards)}${p.devCards ? ` · výzkum ${p.devCards}` : ""}${p.knights ? ` · lodě ${p.knights}` : ""}</span>
          ${badges.length ? `<span class="kol-badges">${badges.join(", ")}</span>` : ""}
        </div>`;
      })
      .join("")}</div>`;
  }

  private mapSvg(v: KolonieView): string {
    const parts: string[] = [];
    const pick = this.model.pick;
    const pickTargets = pick ? new Set(targets(v, pick)) : new Set<number>();

    // Pole
    T.hexes.forEach((h, i) => {
      const pts = h.corners.map((c) => `${fmt(T.corners[c].x)},${fmt(T.corners[c].y)}`).join(" ");
      const hex = v.hexes[i];
      const isTarget = pick === "robber" && pickTargets.has(i);
      parts.push(`<polygon class="kol-hex${isTarget ? " is-target" : ""}" points="${pts}" fill="${TERRAIN_FILL[hex.terrain]}"${isTarget ? ` data-hex="${i}"` : ""}/>`);
      parts.push(`<text class="kol-terrain" x="${h.x}" y="${fmt(h.y - 0.52)}">${TERRAIN_LABEL[hex.terrain]}</text>`);
      if (hex.number) {
        const red = hex.number === 6 || hex.number === 8;
        parts.push(`<circle class="kol-token" cx="${h.x}" cy="${fmt(h.y + 0.08)}" r="0.38"/>`);
        parts.push(`<text class="kol-num${red ? " red" : ""}" x="${h.x}" y="${fmt(h.y + 0.2)}">${hex.number}</text>`);
        const n = pips(hex.number);
        for (let k = 0; k < n; k++) parts.push(`<circle class="kol-pip${red ? " red" : ""}" cx="${fmt(h.x + (k - (n - 1) / 2) * 0.09)}" cy="${fmt(h.y + 0.34)}" r="0.032"/>`);
      }
      if (i === v.robber) {
        parts.push(`<g class="kol-robber"><circle cx="${fmt(h.x + 0.5)}" cy="${fmt(h.y - 0.1)}" r="0.24"/><text x="${fmt(h.x + 0.5)}" y="${fmt(h.y - 0.02)}">P</text></g>`);
      }
    });

    // Obchodní stanice
    for (const port of v.ports) {
      const e = T.edges[port.edge];
      const len = Math.hypot(e.x, e.y);
      const px = fmt(e.x + (e.x / len) * 0.8);
      const py = fmt(e.y + (e.y / len) * 0.8);
      for (const c of e.corners) parts.push(`<line class="kol-port-line" x1="${px}" y1="${py}" x2="${T.corners[c].x}" y2="${T.corners[c].y}"/>`);
      const color = port.type === "any" ? "#ffffff" : TERRAIN_FILL[port.type];
      parts.push(`<circle class="kol-port" cx="${px}" cy="${py}" r="0.34" fill="${color}"/>`);
      parts.push(`<text class="kol-port-text" x="${px}" y="${fmt(py + 0.02)}">${port.type === "any" ? "3:1" : "2:1"}</text>`);
      if (port.type !== "any") parts.push(`<text class="kol-port-res" x="${px}" y="${fmt(py + 0.22)}">${TERRAIN_LABEL[port.type]}</text>`);
    }

    // Tunely
    v.edges.forEach((o, i) => {
      if (o === null) return;
      const [a, b] = T.edges[i].corners;
      const line = `x1="${T.corners[a].x}" y1="${T.corners[a].y}" x2="${T.corners[b].x}" y2="${T.corners[b].y}"`;
      parts.push(`<line class="kol-road-bg" ${line}/><line class="kol-road${this.flash.edge === i ? " flash" : ""}" stroke="${PLAYER_COLORS[o]}" ${line}/>`);
    });

    // Výběr hrany
    if (pick === "road" || pick === "freeRoad" || pick === "setupRoad") {
      for (const i of pickTargets) {
        const [a, b] = T.edges[i].corners;
        const line = `x1="${T.corners[a].x}" y1="${T.corners[a].y}" x2="${T.corners[b].x}" y2="${T.corners[b].y}"`;
        parts.push(`<line class="kol-edge-target" stroke="${PLAYER_COLORS[v.me!]}" ${line}/><line class="kol-hit" ${line} data-edge="${i}"/>`);
      }
    }

    // Stavby
    v.corners.forEach((b, c) => {
      if (!b) return;
      const { x, y } = T.corners[c];
      const fill = PLAYER_COLORS[b.owner];
      const cls = `kol-building${this.flash.corner === c ? " flash" : ""}`;
      if (b.city) {
        parts.push(`<g class="${cls}"><rect x="${fmt(x - 0.24)}" y="${fmt(y - 0.12)}" width="0.48" height="0.28" fill="${fill}"/><circle cx="${x}" cy="${fmt(y - 0.12)}" r="0.17" fill="${fill}"/></g>`);
      } else {
        parts.push(`<polygon class="${cls}" points="${fmt(x)},${fmt(y - 0.24)} ${fmt(x + 0.19)},${fmt(y - 0.06)} ${fmt(x + 0.19)},${fmt(y + 0.17)} ${fmt(x - 0.19)},${fmt(y + 0.17)} ${fmt(x - 0.19)},${fmt(y - 0.06)}" fill="${fill}"/>`);
      }
    });

    // Výběr rohu
    if (pick === "setupSettlement" || pick === "settlement" || pick === "city") {
      for (const c of pickTargets) {
        const { x, y } = T.corners[c];
        parts.push(`<circle class="kol-corner-target" cx="${x}" cy="${y}" r="0.24" stroke="${PLAYER_COLORS[v.me!]}"/><circle class="kol-hit" cx="${x}" cy="${y}" r="0.45" data-corner="${c}"/>`);
      }
    }

    // Kurzor zvukové vrstvy (v režimu zvuk i obraz)
    if (this.showCursor) {
      const { x, y } = T.corners[this.model.cursor];
      parts.push(`<circle class="kol-cursor" cx="${x}" cy="${y}" r="0.33"/>`);
      if (this.model.edge !== null) {
        const [a, b] = T.edges[this.model.edge].corners;
        parts.push(`<line class="kol-cursor-edge" x1="${T.corners[a].x}" y1="${T.corners[a].y}" x2="${T.corners[b].x}" y2="${T.corners[b].y}"/>`);
      }
    }

    return `<svg class="kol-map" viewBox="-5.6 -5.3 11.2 10.6" xmlns="http://www.w3.org/2000/svg">${parts.join("")}</svg>`;
  }

  private handHtml(v: KolonieView): string {
    if (!v.hand) return "";
    const chips = RESOURCES.map(
      (r) => `<span class="kol-chip"><span class="kol-chip-color" data-terrain="${r}"></span><span class="kol-chip-name">${resName(r)}</span><span class="kol-chip-count">${v.hand![r]}</span></span>`,
    ).join("");
    return `<div class="kol-hand">${chips}</div>`;
  }

  private tradeHtml(v: KolonieView): string {
    const t = v.trade;
    if (!t || v.me === null) return "";
    const from = v.players[t.from].name;
    if (t.from === v.me) {
      const rows = v.players
        .map((p, i) => {
          if (i === v.me || (t.to !== null && t.to !== i)) return "";
          const r = t.responses[i];
          if (!r) return `<div class="kol-resp">${esc(p.name)}: čeká se</div>`;
          if (r.kind === "decline") return `<div class="kol-resp">${esc(p.name)}: odmítá</div>`;
          const terms = r.kind === "counter" ? `protinabídka: dáš ${bagText(r.give, true)}, dostaneš ${bagText(r.want, true)}` : "přijímá";
          return `<div class="kol-resp">${esc(p.name)}: ${terms} <span class="kol-btn small" data-act="confirm" data-p="${i}">Obchodovat</span></div>`;
        })
        .join("");
      return `<div class="kol-trade"><div class="kol-trade-title">Tvoje nabídka: dáš ${bagText(t.give, true)}, chceš ${bagText(t.want, true)}</div>${rows}<span class="kol-btn small" data-act="cancelTrade">Zrušit nabídku</span></div>`;
    }
    const mine = myPending(v).some((p) => p.kind === "tradeResponse");
    const answered = t.responses[v.me!];
    const canPay = RESOURCES.every((r) => v.hand![r] >= (t.want[r] ?? 0));
    const status = answered ? `<div>Tvoje odpověď: ${answered.kind === "accept" ? "přijímáš" : answered.kind === "decline" ? "odmítáš" : "protinabídka"}.</div>` : "";
    const buttons = mine
      ? `<span class="kol-btn${canPay ? "" : " disabled"}" data-act="${canPay ? "accept" : ""}">Přijmout</span><span class="kol-btn" data-act="decline">Odmítnout</span><span class="kol-btn" data-act="counter">Protinabídka</span>`
      : "";
    return `<div class="kol-trade"><div class="kol-trade-title">${esc(from)} nabízí: dá ${bagText(t.give, true)}, chce ${bagText(t.want, true)}</div>${status}<div class="kol-buttons">${buttons}</div></div>`;
  }

  private actionsHtml(v: KolonieView): string {
    const kinds = myPending(v).map((p) => p.kind);
    const btn = (act: string, label: string, enabled = true, extra = "") =>
      `<span class="kol-btn${enabled ? "" : " disabled"}" data-act="${enabled ? act : ""}"${extra}>${label}</span>`;
    const out: string[] = [];
    if (this.model.pick && ["road", "settlement", "city"].includes(this.model.pick)) out.push(btn("cancelPick", "Zrušit stavbu"));
    if (kinds.includes("roll")) {
      out.push(btn("roll", "Hodit kostkami"));
      if (v.devHand.length) out.push(btn("panel", `Karty výzkumu (${v.devHand.length})`, true, ' data-kind="cards"'));
    }
    if (kinds.includes("roadBuilding")) out.push(btn("end", "Hotovo, další tunel nestavím"));
    if (kinds.includes("steal")) {
      for (const p of v.stealFrom) out.push(btn("steal", `Ukrást: ${esc(v.players[p].name)} (${cards(v.players[p].cards)})`, true, ` data-p="${p}"`));
    }
    if (kinds.includes("main")) {
      const can = (cost: Bag, kind: PickKind) => afford(v, cost) && targets(v, kind).length > 0;
      out.push(btn("pick", `Tunel<small>${bagText(COSTS.tunel)}</small>`, can(COSTS.tunel, "road") && v.players[v.me!].pieces.tunel > 0, ' data-kind="road"'));
      out.push(btn("pick", `Základna<small>${bagText(COSTS.zakladna)}</small>`, can(COSTS.zakladna, "settlement") && v.players[v.me!].pieces.zakladna > 0, ' data-kind="settlement"'));
      out.push(btn("pick", `Město<small>${bagText(COSTS.mesto)}</small>`, can(COSTS.mesto, "city") && v.players[v.me!].pieces.mesto > 0, ' data-kind="city"'));
      out.push(btn("buyDev", `Výzkum<small>${bagText(COSTS.vyzkum)}</small>`, afford(v, COSTS.vyzkum) && v.devDeck > 0));
      out.push(btn("panel", `Karty výzkumu (${v.devHand.length})`, v.devHand.length > 0, ' data-kind="cards"'));
      out.push(btn("panel", "Banka", RESOURCES.some((r) => v.hand![r] >= rateText(v, r)), ' data-kind="bank"'));
      out.push(btn("panel", "Obchod", !v.trade && bagTotal(v.hand!) > 0, ' data-kind="trade"'));
      out.push(btn("end", "Konec tahu"));
    }
    if (!out.length && v.devHand.length) out.push(btn("panel", `Karty výzkumu (${v.devHand.length})`, true, ' data-kind="cards"'));
    return out.length ? `<div class="kol-actions">${out.join("")}</div>` : "";
  }

  private panelHtml(v: KolonieView): string {
    const p = this.panel;
    if (!p) return "";
    const close = `<span class="kol-btn" data-act="close">Zavřít</span>`;
    const resRow = (r: Res, inner: string) => `<div class="kol-row"><span class="kol-chip-color" data-terrain="${r}"></span><span class="kol-row-name">${resName(r)}</span>${inner}</div>`;
    let body = "";
    switch (p.kind) {
      case "discard": {
        const need = v.discardNeeded;
        const n = bagTotal(p.bag);
        body = `<div class="kol-panel-title">Padla sedmička: odhoď ${cards(need)}</div>${RESOURCES.map((r) =>
          resRow(r, `<span class="kol-have">máš ${v.hand![r]}</span><span class="kol-btn small" data-act="discardSub" data-res="${r}">−</span><span class="kol-count">${p.bag[r] ?? 0}</span><span class="kol-btn small" data-act="discardAdd" data-res="${r}">+</span>`),
        ).join("")}<span class="kol-btn${n === need ? "" : " disabled"}" data-act="${n === need ? "discardSend" : ""}">Odhodit (${n} z ${need})</span>`;
        return `<div class="kol-panel">${body}</div>`;
      }
      case "bank": {
        if (!p.give) {
          body = `<div class="kol-panel-title">Co dáš bance?</div>${RESOURCES.map((r) => {
            const rate = rateText(v, r);
            const ok = v.hand![r] >= rate;
            return `<span class="kol-btn${ok ? "" : " disabled"}" data-act="${ok ? "bankGive" : ""}" data-res="${r}">${rate} ${resName(r)} (máš ${v.hand![r]})</span>`;
          }).join("")}`;
        } else {
          body = `<div class="kol-panel-title">Za ${rateText(v, p.give)} ${resName(p.give)} dostaneš:</div>${RESOURCES.filter((r) => r !== p.give)
            .map((r) => `<span class="kol-btn${v.bank[r] > 0 ? "" : " disabled"}" data-act="${v.bank[r] > 0 ? "bankGet" : ""}" data-res="${r}">1 ${resName(r)}</span>`)
            .join("")}`;
        }
        break;
      }
      case "trade": {
        const row = (r: Res) =>
          resRow(
            r,
            `<span class="kol-side">dáš</span><span class="kol-btn small" data-act="tradeSub" data-side="give" data-res="${r}">−</span><span class="kol-count">${p.give[r] ?? 0}</span><span class="kol-btn small" data-act="tradeAdd" data-side="give" data-res="${r}">+</span>
             <span class="kol-side">chceš</span><span class="kol-btn small" data-act="tradeSub" data-side="want" data-res="${r}">−</span><span class="kol-count">${p.want[r] ?? 0}</span><span class="kol-btn small" data-act="tradeAdd" data-side="want" data-res="${r}">+</span>`,
          );
        const ok = bagTotal(p.give) > 0 && bagTotal(p.want) > 0;
        body = `<div class="kol-panel-title">${p.counterTo !== null ? "Protinabídka" : "Nabídka ostatním"}</div>${RESOURCES.map(row).join("")}
          <span class="kol-btn${ok ? "" : " disabled"}" data-act="${ok ? "tradeSend" : ""}">${p.counterTo !== null ? "Poslat protinabídku" : "Nabídnout"}</span>`;
        break;
      }
      case "cards": {
        const myTurn = myPending(v).some((x) => x.kind === "main" || x.kind === "roll");
        body = `<div class="kol-panel-title">Tvoje karty výzkumu</div>${
          v.devHand.length
            ? v.devHand
                .map((d) => {
                  const ok = myTurn && !d.fresh && d.kind !== "bod" && !v.devPlayedThisTurn && (d.kind !== "tunely" || targets(v, "road").length > 0);
                  const why = d.kind === "bod" ? "počítá se sama" : d.fresh ? "koupená v tomto tahu" : v.devPlayedThisTurn ? "v tomto tahu už byla zahraná jedna karta" : "";
                  return `<div class="kol-card"><b>${DEV_NAME[d.kind]}</b>: ${DEV_HELP[d.kind]} ${ok ? `<span class="kol-btn small" data-act="play" data-kind="${d.kind}">Zahrát</span>` : why ? `<i>(${why})</i>` : ""}</div>`;
                })
                .join("")
            : "<div>Nemáš žádné karty.</div>"
        }`;
        break;
      }
      case "yop":
        body = `<div class="kol-panel-title">Vyber 2 suroviny${p.picks.length ? ` (první: ${resName(p.picks[0])})` : ""}</div>${RESOURCES.map((r) => `<span class="kol-btn" data-act="yopPick" data-res="${r}">${resName(r)}</span>`).join("")}`;
        break;
      case "monopoly":
        body = `<div class="kol-panel-title">Monopol: kterou surovinu ti všichni dají?</div>${RESOURCES.map((r) => `<span class="kol-btn" data-act="monopolyPick" data-res="${r}">${resName(r)}</span>`).join("")}`;
        break;
    }
    return `<div class="kol-panel">${body}${close}</div>`;
  }
}
